import { useState } from 'react';
import { SendHorizonal } from 'lucide-react';

import './aiCopilot.css';

import SuggestedPrompts from './SuggestedPrompts';
import ChatMessage from './ChatMessage';

import { askAICopilot, getCachedAnswer } from '../../utilities/api/aiCopilot/aiCopilotApi';

import { MainLayout } from '../../components/Layout';

const AICopilot = ({ collapsed, setCollapsed }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);

  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content:
        "Hi! I'm your MSIS AI Copilot. Ask me anything about your makerspace analytics.",
    },
  ]);

  const sendQuestion = async (customQuestion = null, isSuggested = false) => {
    const finalQuestion = customQuestion || question;

    if (!finalQuestion.trim()) return;

    const userMessage = {
      type: 'user',
      content: finalQuestion,
    };

    setMessages((prev) => [...prev, userMessage]);

    setQuestion('');
    setLoading(true);

    try {
      // Build history from all messages before this one (excluding the greeting)
      const history = messages
        .filter((m) => m.content)
        .map((m) => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content,
          data: m.data || [],
        }));

      // Add empty AI message first for streaming
      let aiMessageIndex;

      setMessages((prev) => {
        aiMessageIndex = prev.length + 1;

        return [
          ...prev,
          {
            type: 'ai',
            content: '',
          },
        ];
      });

      const response = await askAICopilot(finalQuestion, history, isSuggested);

      console.log('AI Response:', response);

      // STREAMING EFFECT
      const fullResponse =
        response?.response ||
        response?.ai_response ||
        response?.message ||
        'We are currently unable to process your request. Please try again after some time.';

      let currentText = '';

      const responseData = response?.data || [];

      for (let i = 0; i < fullResponse.length; i++) {
        currentText += fullResponse[i];

        setMessages((prev) => {
          const updated = [...prev];

          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: currentText,
            data: responseData,
          };

          return updated;
        });

        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      setMessages((prev) => {
        const updated = [...prev];

        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          followUps: response?.follow_ups || [],
        };

        return updated;
      });
    } catch (error) {
      console.error('AI Copilot Error:', error);

      const errorMessage = {
        type: 'ai',
        content: 'Something went wrong while processing your request.',
      };

      setMessages((prev) => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  // Follow-up chips are served straight from the cache: no LLM call, so the
  // answer renders instantly instead of the fake typewriter effect above.
  const selectFollowUp = async (followUp) => {
    const userMessage = {
      type: 'user',
      content: followUp.question,
    };

    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await getCachedAnswer(followUp.id);

      setMessages((prev) => [
        ...prev,
        {
          type: 'ai',
          content: response?.ai_response || '',
          data: response?.data || [],
          followUps: response?.follow_ups || [],
        },
      ]);
    } catch (error) {
      console.error('AI Copilot Cached Follow-Up Error:', error);

      setMessages((prev) => [
        ...prev,
        {
          type: 'ai',
          content: 'Something went wrong while fetching this answer.',
        },
      ]);
    }

    setLoading(false);
  };

  return (
    <MainLayout
      collapsed={collapsed}
      setCollapsed={setCollapsed}
    >
      <div className="copilot-page">
        <div className="copilot-header">
          <h1>AI Copilot</h1>
        </div>

        <SuggestedPrompts onSelect={sendQuestion} />

        <div className="chat-container">
          {messages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message}
              onSelectFollowUp={selectFollowUp}
            />
          ))}

          {loading && (
            <div className="typing-loader">
              AI Copilot is thinking...
            </div>
          )}
        </div>

        <div className="chat-input-container">
          <input
            type="text"
            placeholder="Ask a question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                sendQuestion();
              }
            }}
          />

          <button onClick={() => sendQuestion()}>
            <SendHorizonal size={20} />
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default AICopilot;